import { clearAllProgressAnchors } from '@/shared/lib/progress';
import { lastProcessingJobs } from './internals';
import { resetWatchdogProbeMap } from '../../lib/watchdog';
import type { JobQueueStateCreator, StateSlice } from './types';

export const createStateSlice: JobQueueStateCreator<StateSlice> = (
  set,
  get
) => ({
  jobQueue: [],
  unseenCompletedJobIds: new Set<string>(),
  queueControls: [],
  isLoadingQueue: false,
  lastQueueFetch: null,
  queueAggregate: null,
  preloadingRenders: {},

  setQueueAggregate: (aggregate) => set({ queueAggregate: aggregate }),

  startRenderPreload: (renderId, conceptAngleId) =>
    set((state) => ({
      preloadingRenders: {
        ...state.preloadingRenders,
        [renderId]: conceptAngleId,
      },
    })),

  finishRenderPreload: (renderId) =>
    set((state) => {
      if (!(renderId in state.preloadingRenders)) return state;
      const next = { ...state.preloadingRenders };
      delete next[renderId];
      return { preloadingRenders: next };
    }),

  setLoadingQueue: (loading) => set({ isLoadingQueue: loading }),

  setLastQueueFetch: (date) => set({ lastQueueFetch: date }),

  markJobsSeen: (conceptId) => {
    set((state) => {
      if (!conceptId) {
        // Clear all unseen
        if (state.unseenCompletedJobIds.size === 0) return state;
        return { unseenCompletedJobIds: new Set<string>() };
      }
      // Clear unseen for specific concept
      const newUnseen = new Set<string>();
      state.unseenCompletedJobIds.forEach((jobId) => {
        const job = state.jobQueue.find((j) => j.id === jobId);
        if (job && job.conceptId !== conceptId) {
          newUnseen.add(jobId);
        }
      });
      if (newUnseen.size === state.unseenCompletedJobIds.size) return state;
      return { unseenCompletedJobIds: newUnseen };
    });
  },

  getUnseenCompletedCount: () => get().unseenCompletedJobIds.size,

  /**
   * Reset store to initial state
   * Called during logout to clear all job and render data
   */
  reset: () => {
    // Clear protection caches and progress anchors
    lastProcessingJobs.clear();
    clearAllProgressAnchors();
    // L4 watchdog per-job probe timestamps must not leak across logout/login.
    resetWatchdogProbeMap();

    set({
      jobQueue: [],
      unseenCompletedJobIds: new Set<string>(),
      queueControls: [],
      isLoadingQueue: false,
      lastQueueFetch: null,
      queueAggregate: null,
      preloadingRenders: {},
    });
  },
});
